// src/pages/tools/index.tsx

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Tool } from "@/api/tools/tools.types";
import { getTools } from "@/api/tools/tools.api";
import { useEnhancedToast } from "@/components/ui/enhanced-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Combobox } from "@/components/ui/combobox";
import { SiteHeader } from "@/components/site-header";
import { EmptyState as SharedEmptyState } from "@/components/ui/empty-state";
import { 
    Plus, 
    ShieldAlert, 
    WrenchIcon,
    AlertCircle,
    Search,
    SearchX,
    Lock
} from "lucide-react";
import { ToolCard } from "./components/ToolCard";
import { ToolCardSkeleton } from "./components/ToolCardSkeleton";
import { ToolsSkeleton } from "./components/ToolsSkeleton";
import { CreateToolTypeDialog } from "./components/CreateToolTypeDialog";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    TooltipProvider,
} from "@/components/ui/tooltip";
import { containerVariants, itemVariants } from "@/components/animations";
import { MobileOverlay } from "@/components/ui/mobile-overlay";
import { useIsMobile } from "@/hooks/use-mobile";

const TYPE_OPTIONS = [
    { label: "All Types", value: "" },
    { label: "Function", value: "function" },
    { label: "DTMF", value: "dtmf" },
    { label: "End Call", value: "endCall" },
    { label: "Transfer", value: "transferCall" },
    { label: "Output", value: "output" },
    { label: "MCP", value: "mcp" },
    { label: "Knowledge", value: "query" },
    { label: "Voicemail", value: "voicemail" },
    { label: "TransAssist", value: "handoff" }
];

const Tools = () => {
    const [tools, setTools] = useState<Tool[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const navigate = useNavigate();
    const { error } = useEnhancedToast();
    const { user } = useAuth();
    const isMobile = useIsMobile();

    // Create refs for toast functions
    const errorRef = useRef(error);

    // Update refs when toast functions change
    useEffect(() => {
        errorRef.current = error;
    }, [error]);

    const canCreateTools = user?.permissions?.tools === "EDITOR";

    // Filter tools based on search and type
    const filteredTools = useMemo(() => {
        return tools.filter(tool => {
            const matchesSearch = searchQuery === "" || 
                tool.function?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                tool.function?.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                tool.id?.toLowerCase().includes(searchQuery.toLowerCase());
            
            const matchesType = typeFilter === "" || tool.type === typeFilter;
            
            return matchesSearch && matchesType;
        });
    }, [tools, searchQuery, typeFilter]);

    const hasActiveFilters = searchQuery !== "" || typeFilter !== "";

    const fetchTools = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getTools();
            if (response.error) throw new Error(response.error);
            setTools(response.data || []);
        } catch (err) {
            errorRef.current({
                icon: AlertCircle,
                title: "Failed to Load Tools",
                description: err instanceof Error ? err.message : "Could not fetch available tools",
                duration: 4000,
            });
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTools();
    }, [fetchTools]);

    const handleCreateNew = () => {
        if (!canCreateTools) return;
        setCreateDialogOpen(true);
    };

    const handleClearFilters = () => {
        setSearchQuery("");
        setTypeFilter("");
    };

    return (
        <>
            <div className="flex flex-col h-full">
                <SiteHeader
                    title="Tools"
                    subtitle="Create and manage your custom tools"
                >
                    {/* Only show search and filters when tools exist */}
                    {tools.length > 0 && (
                        <>
                            {/* Search Input */}
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                <Input
                                    placeholder="Search tools..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="h-9 w-[200px] lg:w-[250px] pl-9"
                                />
                            </div>

                            {/* Type Filter */}
                            <Combobox
                                options={TYPE_OPTIONS}
                                value={typeFilter}
                                onValueChange={setTypeFilter}
                                placeholder="Filter by type..."
                                className="w-[160px]"
                            />

                            {/* New Tool Button */}
                            <TooltipProvider delayDuration={200}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span>
                                            <Button
                                                onClick={handleCreateNew}
                                                disabled={!canCreateTools}
                                                size="sm"
                                                className="gap-2"
                                            >
                                                {canCreateTools ? (
                                                    <>
                                                        <Plus className="h-4 w-4" />
                                                        New Tool
                                                    </>
                                                ) : (
                                                    <>
                                                        <Lock className="h-4 w-4" />
                                                        New Tool
                                                    </>
                                                )}
                                            </Button>
                                        </span>
                                    </TooltipTrigger>
                                    {!canCreateTools && (
                                        <TooltipContent side="bottom" sideOffset={5} className="flex items-center gap-2 max-w-xs">
                                            <ShieldAlert className="w-12 h-12 text-amber-500" />
                                            <p>You need editor permissions to create tools. Contact your administrator for access.</p>
                                        </TooltipContent>
                                    )}
                                </Tooltip>
                            </TooltipProvider>
                        </>
                    )}
                </SiteHeader>

                <div className="flex-1 overflow-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-4 py-6 md:px-8 md:py-8"
                    >
                        {isLoading ? (
                            <ToolsSkeleton />
                        ) : tools.length === 0 ? (
                            <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 200px)' }}>
                                <SharedEmptyState
                                    icon={WrenchIcon}
                                    title="No Tools Yet"
                                    description="Tools extend your assistant's capabilities. Create custom tools or integrate with external services."
                                    action={
                                        canCreateTools ? (
                                            <Button onClick={handleCreateNew} className="gap-2">
                                                <Plus className="h-4 w-4" />
                                                Create Your First Tool
                                            </Button>
                                        ) : undefined
                                    }
                                />
                            </div>
                        ) : filteredTools.length === 0 ? (
                            <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 200px)' }}>
                                <div className="text-center space-y-3">
                                    <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                                        <SearchX className="h-8 w-8 text-muted-foreground" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold mb-2">No Matching Tools</h3>
                                        <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                                            No tools found matching your search criteria. Try adjusting your filters.
                                        </p>
                                        <Button variant="outline" size="sm" onClick={handleClearFilters} className="gap-2">
                                            <SearchX className="h-4 w-4" />
                                            Clear All Filters
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <motion.div
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                            >
                                {filteredTools.map((tool, index) => (
                                    <motion.div
                                        key={tool.id || `tool-${index}`}
                                        variants={itemVariants}
                                    >
                                        <ToolCard
                                            tool={tool}
                                            onClick={() => navigate(`/dashboard/tools/${tool.id}`)}
                                        />
                                    </motion.div>
                                ))}
                            </motion.div>
                        )}
                    </motion.div>
                </div>
            </div>

            {/* Mobile Overlay */}
            {isMobile && (
                <MobileOverlay
                    icon={WrenchIcon}
                    title="Desktop View Only"
                    description="Tool management is currently optimized for desktop view. Please use a desktop browser for the best experience."
                    onBack={() => navigate('/dashboard')}
                />
            )}

            {/* Create Tool Type Dialog */}
            <CreateToolTypeDialog
                open={createDialogOpen}
                onOpenChange={setCreateDialogOpen}
            />
        </>
    );
};

export default Tools;